import { Button, Tooltip, TooltipTrigger, TooltipContent, Badge, cn } from '@design-system';
import {
  AnimatedDataPopup,
  CircularProgress,
  YoungGirlIllustration,
  YoungBoyIllustration,
  MiddleAgeWomanIllustration,
  MiddleAgeManIllustration,
  AgedWomanIllustration,
  useAnimationPhases,
} from '@/features/client-onboarding';
import { LikelihoodSlideOutContent, LtcAtAgeSlideOutContent } from '@client/components';

import { RotateCcw, SkipForward } from 'lucide-react';

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { startCase } from 'lodash';
import { pronoun, possessivePronoun } from '@client/util';

interface FunctionalStatePhase {
  id: string;
  duration: number;
  illustrationType: string;
  title: string;
  label: ReactNode;
  description: ReactNode;
  illustration: any;
}

function getDecade(age: number): string {
  const decade = Math.floor(age / 10) * 10;
  return `${decade}s`;
}

const DAILY_ACTIVITIES_TOOLTIP =
  'Self-care activities include bathing, dressing, eating, transferring (e.g., from bed to chair), toileting, and continence.';

function DailyActivitiesTooltip({ children }: { children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline cursor-help underline decoration-dotted underline-offset-2">
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">{DAILY_ACTIVITIES_TOOLTIP}</TooltipContent>
    </Tooltip>
  );
}

function getPhases({
  clientName,
  intakeSurvey,
  ltcAtAge,
}: {
  clientName: string;
  intakeSurvey: IntakeSurvey;
  ltcAtAge: number;
}): readonly FunctionalStatePhase[] {
  const subjectPronoun = pronoun(intakeSurvey);
  const possessive = possessivePronoun(intakeSurvey);
  const isPlural = subjectPronoun === 'they';
  const verbIs = isPlural ? 'are' : 'is';
  const verbNeeds = isPlural ? 'need' : 'needs';

  const isMale = subjectPronoun === 'he';
  const ltcDecade = getDecade(ltcAtAge);
  const preDecade = getDecade(ltcAtAge - 10);

  return [
    {
      id: 'independent_cautious',
      duration: 2500,
      illustrationType: 'A',
      title: 'Current State',
      label: 'Independent and fully capable',
      description: `As ${clientName} continues to age`,
      illustration: isMale ? YoungBoyIllustration : YoungGirlIllustration,
    },
    {
      id: 'independent_cautious_alt',
      duration: 2500,
      illustrationType: 'B',
      title: `By ${possessive} ${preDecade}`,
      label: `${startCase(subjectPronoun)} ${verbIs} slowing down but still capable`,
      description: `${startCase(subjectPronoun)} naturally ${verbNeeds} assistance as ${subjectPronoun} slows down`,
      illustration: isMale ? MiddleAgeManIllustration : MiddleAgeWomanIllustration,
    },
    {
      id: 'needs_assistance',
      duration: 2500,
      illustrationType: 'C',
      title: 'In need of LTC help',
      label: (
        <>
          Unable to perform <DailyActivitiesTooltip>daily activities</DailyActivitiesTooltip> alone
        </>
      ),
      description: (
        <>
          We predict, in {possessive} {ltcDecade}, {clientName} will need help with{' '}
          <DailyActivitiesTooltip>daily activities</DailyActivitiesTooltip>
        </>
      ),
      illustration: AgedWomanIllustration,
    },
  ];
}

interface FunctionalStateAnimationProps {
  clientName: string;
  ltcLikelihood: number;
  ltcAtAge: number;
  intakeSurvey: IntakeSurvey;
  ltcAtAgeIsEdited?: boolean;
}

function IllustrationCard({
  title,
  description,
  children,
  isVisible,
  isActive,
}: {
  title: string;
  description: ReactNode;
  children: ReactNode;
  isVisible: boolean;
  isActive: boolean;
}) {
  return (
    <div
      className={cn(
        'z-10 flex h-full translate-y-8 flex-col items-center opacity-0 transition-all duration-700 ease-out',
        {
          'translate-y-0 opacity-100': isVisible,
        },
      )}
    >
      <div
        className={cn(
          'bg-card relative flex h-full w-full flex-col justify-between overflow-hidden rounded-xl border transition-all duration-500',
          { 'border-primary': isActive },
        )}
      >
        <div className="bg-linear-to-b from-muted to-background flex items-center justify-center p-4">
          {children}
        </div>
        <div className="px-4 pb-4">
          <p
            className={cn(
              'text-foreground mb-1 text-balance text-start text-sm font-medium transition-colors duration-300',
              { 'text-primary': isActive },
            )}
          >
            {title}
          </p>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
      </div>
    </div>
  );
}

export function FunctionalStateAnimation({
  clientName,
  ltcLikelihood,
  ltcAtAge,
  intakeSurvey,
  ltcAtAgeIsEdited,
}: FunctionalStateAnimationProps) {
  const phases = getPhases({ clientName, intakeSurvey, ltcAtAge });

  const { currentPhaseIndex, currentPhase, isComplete, replay, skip, isAutoPlaying } =
    useAnimationPhases({ phases });

  const isLastPhase = currentPhaseIndex === phases.length - 1;

  const prevPhaseIndex = useRef(currentPhaseIndex);
  const [exitingIndex, setExitingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (currentPhaseIndex !== prevPhaseIndex.current) {
      setExitingIndex(prevPhaseIndex.current);
      const timer = setTimeout(() => setExitingIndex(null), 400);
      prevPhaseIndex.current = currentPhaseIndex;
      return () => clearTimeout(timer);
    }
  }, [currentPhaseIndex]);

  return (
    <div className="flex w-full flex-col items-center gap-6">
      <div className="flex w-full items-center justify-between gap-4">
        <p
          key={currentPhase.id}
          className="animate-fade-in text-foreground max-w-3xl text-balance text-sm leading-relaxed lg:text-base"
        >
          {currentPhase.description}
        </p>
        <CircularProgress
          currentIndex={currentPhaseIndex}
          totalSteps={phases.length}
          duration={phases[currentPhaseIndex].duration}
        />
      </div>

      {/* Desktop: horizontal row */}
      <div className="relative hidden w-full max-w-5xl lg:block">
        <div className="relative flex h-max justify-center gap-8">
          {phases.map((phase, index) => {
            const isVisible = index <= currentPhaseIndex;
            const isActive = index === currentPhaseIndex;
            const isLast = index === phases.length - 1;
            const Illustration = phase.illustration;

            return (
              <div
                key={phase.id}
                className="relative flex h-full w-full flex-col md:basis-1/3"
                style={{
                  transitionDelay: `${index * 100}ms`,
                }}
              >
                <IllustrationCard
                  isActive={isActive}
                  isVisible={isVisible}
                  title={phase.title}
                  description={phase.label}
                >
                  <Illustration
                    className={cn('size-full text-gray-950', {
                      'text-primary-dark': isActive,
                    })}
                  />
                </IllustrationCard>

                {isLast && isLastPhase && (
                  <div className="scale-80 absolute left-full top-1/2 z-20 -translate-x-[45%] -translate-y-1/2 xl:-translate-x-[25%] xl:scale-100">
                    <div className="flex flex-col gap-4">
                      <AnimatedDataPopup
                        label="Likelihood of needing LTC"
                        value={`${Math.round(ltcLikelihood * 100)}%`}
                        delay={250}
                        slideOutContent={<LikelihoodSlideOutContent />}
                      />
                      <AnimatedDataPopup
                        label="Care expected to begin at age"
                        value={ltcAtAge}
                        delay={500}
                        slideOutContent={<LtcAtAgeSlideOutContent />}
                        badge={ltcAtAgeIsEdited && <Badge variant="secondary">Edited</Badge>}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile: one card at a time with fade-scale transitions */}
      <div className="relative w-full max-w-sm lg:hidden">
        <div className="relative">
          {phases.map((phase, index) => {
            const isActive = index === currentPhaseIndex;
            const isExiting = index === exitingIndex;
            const Illustration = phase.illustration;

            if (!isActive && !isExiting) return null;

            return (
              <div
                key={phase.id}
                className={cn({
                  'animate-fade-scale-in': isActive,
                  'animate-fade-scale-out pointer-events-none absolute inset-0': isExiting,
                })}
              >
                <IllustrationCard
                  isActive
                  isVisible
                  title={phase.title}
                  description={phase.label}
                >
                  <Illustration
                    className={cn('size-full max-h-72 text-gray-950', {
                      'text-primary-dark': isActive,
                    })}
                  />
                </IllustrationCard>
              </div>
            );
          })}
        </div>

        {isLastPhase && (
          <div className="mt-4 flex justify-center gap-4">
            <AnimatedDataPopup
              label="Likelihood of needing LTC"
              value={`${Math.round(ltcLikelihood * 100)}%`}
              delay={250}
              slideOutContent={<LikelihoodSlideOutContent />}
            />
            <AnimatedDataPopup
              label="Care expected to begin at age"
              value={ltcAtAge}
              delay={500}
              slideOutContent={<LtcAtAgeSlideOutContent />}
              badge={ltcAtAgeIsEdited && <Badge variant="secondary">Edited</Badge>}
            />
          </div>
        )}
      </div>

      <div className="flex w-full items-center justify-end gap-4">
        {isAutoPlaying && (
          <Button
            size="sm"
            variant="outline"
            onClick={skip}
          >
            <SkipForward />
            Skip
          </Button>
        )}
        {isComplete && (
          <Button
            size="sm"
            variant="outline"
            onClick={replay}
          >
            <RotateCcw />
            Replay
          </Button>
        )}
      </div>
    </div>
  );
}
